import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import TierBadge from "../../../../public/icons/tier-badge";
import { BrandContext } from "../../../contexts/BrandContext";
import BrandTooltip from "../BrandTooltip";
import ProgressBar from "./ProgressBar";

// This array should be sorted increasingly based on targetPoints
export type MilestoneProgressType = {
  tag: string;
  targetPoints: number;
  tier: string;
}[];

interface MilestoneProgressProps {
  id: string;
  showLifetimePoints?: boolean;
  lifetimePoints: number;
  milestoneProgressData: MilestoneProgressType;
}

const MilestoneProgress: React.FC<MilestoneProgressProps> = ({
  id,
  lifetimePoints,
  milestoneProgressData,
  showLifetimePoints,
}) => {
  const [target, setTarget] = useState(1000);

  const nextTarget = milestoneProgressData.filter(
    (milestone) => milestone.targetPoints > lifetimePoints
  )[0];
  useEffect(() => {
    if (lifetimePoints >= target) {
      setTarget(target + 1000);
    }
  }, [target, lifetimePoints]);

  const brandValue = React.useContext(BrandContext);

  // Sort milestones by target points
  const sortedMilestones = [...milestoneProgressData].sort(
    (a, b) => a.targetPoints - b.targetPoints
  );

  const lastMilestone = sortedMilestones[sortedMilestones.length - 1];

  const getPercentage = useMemo(() => {
    if (!sortedMilestones?.length) return 0;

    const bronze = sortedMilestones.find((m) => m.tier === "bronze");
    const silver = sortedMilestones.find((m) => m.tier === "silver");
    const gold = sortedMilestones.find((m) => m.tier === "gold");

    if (!bronze || !silver || !gold) return 0;

    // If points are below bronze target (1-10%)
    if (lifetimePoints < bronze.targetPoints) {
      const range = bronze.targetPoints;
      const progress = (lifetimePoints / range) * 10;
      return Math.max(1, Math.min(10, Math.round(progress)));
    }

    // If points are between bronze and silver (12-49%)
    if (lifetimePoints < silver.targetPoints) {
      const range = silver.targetPoints - bronze.targetPoints;
      const progress = ((lifetimePoints - bronze.targetPoints) / range) * 37;
      return Math.round(12 + Math.min(progress, 37));
    }

    // If points are between silver and gold (50-88%)
    if (lifetimePoints <= gold.targetPoints) {
      const range = gold.targetPoints - silver.targetPoints;
      const progress = ((lifetimePoints - silver.targetPoints) / range) * 38;
      return Math.round(50 + Math.min(progress, 38));
    }

    return 100;
  }, [lifetimePoints, milestoneProgressData]);

  return (
    <>
      <div className="flex items-center justify-between w-full space-x-2 pb-2">
        {milestoneProgressData.map((milestone, index) => (
          <div key={milestone.tag} className="flex items-center gap-1">
            <BrandTooltip
              id={`${milestone.tag}-tooltip-${id}`}
              tooltip={`${milestone.tag}`}
              place="top"
            >
              <div className=" inline-flex items-center px-2 ">
                <span
                  className={classNames(
                    "text-xs font-semibold leading-none w-[48px] h-[44px]  ",
                    {
                      "opacity-50": lifetimePoints < milestone.targetPoints,
                    }
                  )}
                >
                  {lifetimePoints >= milestone.targetPoints ? (
                    <TierBadge
                      tier={milestone.tier}
                      className="flex-shrink-0 w-[48px] h-[44px] "
                    />
                  ) : (
                    <TierBadge
                      tier={``}
                      imageClassName="w-full h-full"
                      className="flex-shrink-0 w-[48px] h-[44px]"
                    />
                  )}
                </span>
              </div>
            </BrandTooltip>
          </div>
        ))}
      </div>
      <div id="scale" className="flex flex-col w-full space-y-1 ">
        <div className={classNames("")}>
          {/* Progress Bar */}
          <ProgressBar percentage={getPercentage} />
        </div>
        <h1 className="text-xs font-semibold text-center opacity-70">
          {lastMilestone.targetPoints <= lifetimePoints
            ? "You are Gold Medalist! Keep Going!"
            : !!nextTarget?.targetPoints
            ? `${nextTarget.targetPoints - lifetimePoints} ${
                brandValue.currency ?? `Points`
              } To ${nextTarget?.tag}`
            : `${target - lifetimePoints} ${
                brandValue.currency ?? `Points`
              } to next target`}
        </h1>
      </div>
    </>
  );
};

export default React.memo(MilestoneProgress);
